import {
  SHStack,
  SHToggleButtonOptions,
  SHTypography,
} from "@components/design-systems";
import { SHCollapse } from "@components/design-systems/sh-collapse";
import { DateFormat } from "@constants/format";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { StepName } from "@layouts/stepper/step-name";
import { AnalysisVirtualizedStepProps } from "@models/platform-analysis/entities/analysis";
import { FeatureAnalysisScoreMode } from "@models/platform-analysis/enums/feature/featureAnalysisScoreMode";
import { useTheme } from "@mui/material";
import { FeatureAnalysisDisplayScoresOptions } from "@pages/platform-analysis-v2/_id/constants";
import { format } from "date-fns";
import { memo, useEffect, useMemo, useState } from "react";
import { useToggle } from "react-use";
import { AnalysisTableSettings } from "../settings";

type AnalysisTableToolBarProps = {
  title?: string;
  stepNumber: number;
  isDisabled?: boolean;
  hidden?: boolean;
  allowFilters?: boolean;
  virtualizedProps: AnalysisVirtualizedStepProps;
};

export const AnalysisTableToolBar = memo(
  ({
    title,
    isDisabled,
    stepNumber,
    hidden,
    allowFilters,
    virtualizedProps,
  }: AnalysisTableToolBarProps) => {
    const { breakpoints } = useTheme();

    const { platformAnalysis, isShowAnalysisScores, setIsShowAnalysisScores } =
      usePlatformAnalysis();

    const [changedManually, toggleChangedManually] = useToggle(false);
    const [analysisScoresMode, setAnalysisScoresMode] =
      useState<FeatureAnalysisScoreMode>(
        isShowAnalysisScores
          ? FeatureAnalysisScoreMode.Show
          : FeatureAnalysisScoreMode.Hide,
      );

    const handleAnalysisScoresModeChange = (
      value: FeatureAnalysisScoreMode,
    ) => {
      if (value === analysisScoresMode || !value) return;
      toggleChangedManually(true);
      setAnalysisScoresMode(value);
      setIsShowAnalysisScores(value === FeatureAnalysisScoreMode.Show);
    };

    const dataValidDate = useMemo(() => {
      if (platformAnalysis?.databaseVersion) {
        return `Data valid: ${format(
          new Date(platformAnalysis?.databaseVersion),
          DateFormat,
        )}`;
      }
      return "";
    }, [platformAnalysis?.databaseVersion]);

    useEffect(() => {
      if (changedManually) return;
      setAnalysisScoresMode(
        isShowAnalysisScores
          ? FeatureAnalysisScoreMode.Show
          : FeatureAnalysisScoreMode.Hide,
      );
    }, [isShowAnalysisScores, changedManually]);

    return (
      <SHStack
        width={"100%"}
        maxWidth={breakpoints.values.lg2}
        paddingX={{ xs: 0, lg2: 0 }}
        marginTop={"-3px"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <SHStack height={"100%"} width={"100%"}>
          <SHCollapse open={!hidden} displayArrow={false}>
            <SHStack
              height={"100%"}
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              paddingY={"20px"}
            >
              <StepName name={title} stepNumber={stepNumber} />

              <SHStack height={"100%"} justifyContent={"space-between"}>
                <SHTypography
                  variant="body3"
                  colorVariant="third"
                  textAlign={"end"}
                  lineHeight="120%"
                  sx={{ marginTop: "10px" }}
                >
                  {dataValidDate}
                </SHTypography>

                {allowFilters && (
                  <SHStack direction={"row"} gap={2} alignItems="center">
                    <SHToggleButtonOptions
                      disabled={isDisabled}
                      options={FeatureAnalysisDisplayScoresOptions}
                      value={analysisScoresMode}
                      onChange={(_, value: FeatureAnalysisScoreMode) => {
                        handleAnalysisScoresModeChange(value);
                      }}
                    />

                    <AnalysisTableSettings
                      virtualizedProps={virtualizedProps}
                    />
                  </SHStack>
                )}
              </SHStack>
            </SHStack>
          </SHCollapse>
        </SHStack>
      </SHStack>
    );
  },
);
